import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChange } from '@angular/core';
import { FileInfo, InProgressTreatment, TreatmentTypeFile } from 'app/core/models';
import { FileSelectEvent, FileUpload } from 'primeng/fileupload';
import {
  AttachOrderFileModel,
  OrderFilesAttachedEvent,
  OrderFilesAttachProgressEvent,
  OrderFilesRemovedEvent,
} from './model';
import { parseFileModelFromFileInfo } from './parser';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AwsApiClient, TreatmentApiClient } from 'app/core/api-clients';
import { firstValueFrom, takeWhile } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { PrettyBytesPipe } from 'app/shared/pipes';
import { Message } from 'primeng/message';
import { ProgressBar } from 'primeng/progressbar';
import { Button } from 'primeng/button';
import { isFileAccepted, isUnsafeFile, toNativeFileInputAccept } from 'app/core/helpers';

@Component({
  selector: 'wl-attach-order-files',
  standalone: true,
  imports: [CommonModule, FileUpload, TranslatePipe, PrettyBytesPipe, Message, ProgressBar, Button],
  templateUrl: './attach-order-files.component.html',
  styleUrl: './attach-order-files.component.scss',
})
export class AttachOrderFilesComponent implements OnChanges, OnDestroy {
  private readonly _treatmentClient = inject(TreatmentApiClient);
  private readonly _awsClient = inject(AwsApiClient);

  @Input() files?: FileInfo[] | null;
  @Input() order?: Pick<InProgressTreatment, 'id' | 'treatmentTypeId'> | null;
  @Input() typeFile?: TreatmentTypeFile | null;
  @Input() showAllFiles: boolean = false;
  @Input() isRedesignAttachment: boolean = false;

  @Output() readonly attached = new EventEmitter<OrderFilesAttachedEvent>();
  @Output() readonly removed = new EventEmitter<OrderFilesRemovedEvent>();
  @Output() readonly progress = new EventEmitter<OrderFilesAttachProgressEvent>();

  protected fileModels?: AttachOrderFileModel[];
  protected isSelectingFile = false;
  hasInvalidFileFormat = false;
  hasUnsafeFileFormat = false;
  fileUploadAccept = '';

  private uploadingFilesSet = new Set<string>();
  private removingFilesSet = new Set<string>();

  ngOnChanges(changes: Record<keyof AttachOrderFilesComponent, SimpleChange>) {
    if (changes.typeFile) {
      this.fileUploadAccept = toNativeFileInputAccept(this.typeFile?.accept ?? null);
      this.hasInvalidFileFormat = false;
      this.hasUnsafeFileFormat = false;
    }

    if ((changes.files || changes.typeFile || changes.showAllFiles) && this.typeFile) {
      this.parseFileModels();
    }
  }

  ngOnDestroy() {
    this.uploadingFilesSet.clear();
    this.removingFilesSet.clear();
  }

  protected async onSelectFile({ files }: FileSelectEvent): Promise<void> {
    const { typeFile, isSelectingFile } = this;
    if (!typeFile || isSelectingFile) return;

    this.hasUnsafeFileFormat = Array.from(files).some((file: File): boolean => isUnsafeFile(file));
    this.hasInvalidFileFormat = Array.from(files).some(
      (file: File): boolean => !isUnsafeFile(file) && !isFileAccepted(file, typeFile.accept)
    );
    this.isSelectingFile = true;
    try {
      for (const file of files) {
        if (!isFileAccepted(file, typeFile.accept)) {
          continue;
        }

        try {
          const uploadLink = await firstValueFrom(
            this._treatmentClient.getUploadLink(this.order!.id, file.name, false)
          );
          const fileModel: AttachOrderFileModel = {
            name: file.name,
            uploading: false,
            size: file.size,
            id: uploadLink.fileGuid,
            uploadLink: uploadLink.url,
            fileKind: typeFile.fileKind,
            fileType: typeFile.type,
            progress: null,
            rawFile: file,
            uploaded: false,
            fileKey: uploadLink.filename,
          };
          (this.fileModels ??= []).push(fileModel);
          this.startUpload(fileModel);
        } catch (error) {
          console.warn('Failed to prepare selected file for upload.', file.name, error);
        }
      }
    } finally {
      this.isSelectingFile = false;
    }
  }

  private startUpload(fileModel: AttachOrderFileModel) {
    const { uploadLink, rawFile, fileKey, name: fileName, uploading } = fileModel;
    const { order } = this;
    if (!order) return;

    if (uploadLink && !uploading && rawFile != null && fileKey) {
      fileModel.progress = 0;
      fileModel.uploading = true;
      this.uploadingFilesSet.add(fileModel.id!);
      this.reportProgress();

      this._awsClient
        .put(uploadLink, rawFile, 'application/octet-stream')
        .pipe(takeWhile(_ => fileModel.uploading))
        .subscribe({
          next: event => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                fileModel.progress = Math.round((event.loaded * 100) / event.total);
                break;
              case HttpEventType.Response:
                firstValueFrom(
                  this._treatmentClient.saveDesignFileInfo(order.id, {
                    fileGuid: fileModel.id!,
                    fileSize: fileModel.size,
                    fileType: `.${fileName.split('.').pop()}`,
                    filename: fileKey,
                    scanType: fileModel.fileType,
                    subGroup: null,
                    ...(this.isRedesignAttachment ? { isRedesignAttachment: true } : {}),
                  })
                )
                  .then(
                    ({ id, fileGUID, name, size }) => {
                      fileModel.id = `${id}`;
                      fileModel.name = name;
                      fileModel.size = size;
                      fileModel.fileGuid = fileGUID;
                      fileModel.uploaded = true;
                      this.uploadingFilesSet.delete(fileGUID);
                      this.attached.emit({
                        fileGuid: fileGUID,
                      });
                    },
                    error => {
                      fileModel.uploaded = false;
                      this.removeFileModel(fileModel);
                      console.warn('Failed to save uploaded file information.', fileModel.name, error);
                    }
                  )
                  .finally(() => {
                    fileModel.uploading = false;
                    this.uploadingFilesSet.delete(fileModel.id!);
                    this.reportProgress();
                  });

                return event;
            }
          },
          error: error => {
            fileModel.uploading = false;
            fileModel.uploaded = false;
            this.uploadingFilesSet.delete(fileModel.id!);
            this.reportProgress();
            console.warn('Failed to upload selected file.', fileModel.name, error);
          },
        });
    }
  }

  private parseFileModels() {
    const { files, typeFile, fileModels } = this;

    if (typeFile && files) {
      const filteredFiles = this.showAllFiles
        ? files
        : files.filter(f => f.fileType === typeFile.type && (f.fileKind === typeFile.fileKind || f.fileKind == null));
      const fileIds = new Set(filteredFiles.map(f => f.id));
      const parsedFileModels = filteredFiles.map<AttachOrderFileModel>(parseFileModelFromFileInfo);
      if (parsedFileModels.length > 0) {
        this.fileModels = [...(fileModels?.filter(f => f.id == null || !fileIds.has(f.id)) ?? []), ...parsedFileModels];
      }
    }
  }

  private removeFileModel(fileModel: AttachOrderFileModel): void {
    const fileModelIndex = this.fileModels?.indexOf(fileModel) ?? -1;
    if (fileModelIndex >= 0) {
      this.fileModels?.splice(fileModelIndex, 1);
    }

    if (fileModel.id != null) {
      this.uploadingFilesSet.delete(fileModel.id);
      this.removingFilesSet.delete(fileModel.id);
    }
  }

  protected async onRemoveFile(fileModel: AttachOrderFileModel) {
    const { order } = this;
    if (!order) return;
    if (fileModel.removing || !fileModel.id) return;

    fileModel.removing = true;
    this.removingFilesSet.add(fileModel.id!);
    this.reportProgress();
    await firstValueFrom(this._treatmentClient.deleteFile(order.id, fileModel.id)).finally(
      () => (fileModel.removing = false)
    );
    if (this.fileModels) {
      const idx = this.fileModels?.findIndex(f => f.id === fileModel.id);
      this.fileModels?.splice(idx, 1);
      this.removingFilesSet.delete(fileModel.id!);
      this.reportProgress();
      this.removed.emit({
        fileGuid: fileModel.id!,
      });
    }
  }

  private reportProgress() {
    this.progress.emit({
      uploadingFiles: Array.from(this.uploadingFilesSet),
      removingFiles: Array.from(this.removingFilesSet),
      hasFiles: this.fileModels?.some(file => file.uploaded || file.uploading) ?? false,
    });
  }
}
